import React from 'react';
import { Box, Container, Typography, Button, Stack, Fade } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import useMetaTags from '../../hooks/useMetaTags';
import PageHero from '../../components/PageHero/PageHero';
import Footer from '../../components/Footer/Footer';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import VerifiedIcon from '@mui/icons-material/Verified';
import SchoolIcon from '@mui/icons-material/School';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import VisibilityIcon from '@mui/icons-material/Visibility';

interface TeamMember {
  id: number;
  slug: string;
  name: string;
  role: string;
  shortBio: string;
  fullBio: string;
  image: string;
  memberLabel: string;
  badge?: {
    text: string;
    color: string;
  };
  stats?: {
    title: string;
    description: string;
    icon: React.ReactNode;
  }[];
  linkedIn?: string;
}

const teamMembers: TeamMember[] = [
  {
    id: 1,
    slug: 'arnaldo-llerena',
    name: 'Arnaldo Llerena',
    role: 'Fundador',
    memberLabel: 'Fundador',
    image: '/Arnaldo Llerena.jpg',
    shortBio: 'Pionero en impulsar técnicas de agricultura ecológica desde los años 90',
    fullBio: 'Arnaldo Llerena es pionero en impulsar técnicas de agricultura ecológica desde los años 90. Su visión de transformar la agricultura hacia un modelo más sostenible ha sido el motor de Ekkofood desde sus inicios.',
    badge: {
      text: 'Fundador 1999',
      color: '#c2b8a8'
    },
    stats: [
      {
        title: 'Agricultura Ecológica',
        description: 'Impulsó técnicas sostenibles y regenerativas desde la década de los 90.',
        icon: <AgricultureIcon sx={{ color: '#eff1ee' }} />
      },
      {
        title: 'Formación y Talleres',
        description: 'Organiza cursos con expertos internacionales en España y Perú.',
        icon: <SchoolIcon sx={{ color: '#eff1ee' }} />
      }
    ]
  },
  {
    id: 2,
    slug: 'beatriz-cardenas',
    name: 'Beatriz Cárdenas',
    role: 'Coordinación y Nutrición',
    memberLabel: 'Coordinación y Nutrición',
    image: '/Beatriz C - Ekkofood .jpg',
    shortBio: 'Experta en nutrición y agricultura ecológica aplicada a la alimentación saludable',
    fullBio: 'Beatriz Cárdenas es experta en nutrición y agricultura ecológica aplicada a la alimentación saludable. Su experiencia combina el conocimiento técnico con la aplicación práctica en sistemas alimentarios sostenibles.',
    badge: {
      text: 'Experta en Nutrición',
      color: '#c2b8a8'
    },
    stats: [
      {
        title: 'Agricultura Sostenible',
        description: 'Aplica técnicas ecológicas y experimenta con asociaciones de plantas en la finca.',
        icon: <AgricultureIcon sx={{ color: '#eff1ee' }} />
      },
      {
        title: 'Nutrición y Menús',
        description: 'Diseña y supervisa menús nutritivos para retiros y eventos, promoviendo alimentación saludable.',
        icon: <HealthAndSafetyIcon sx={{ color: '#eff1ee' }} />
      }
    ]
  },
  {
    id: 3,
    slug: 'beatriz-llerena',
    name: 'Beatriz Llerena',
    role: 'Cofundadora y Estrategia',
    memberLabel: 'Cofundadora y Estrategia',
    image: '/Beatriz LL - Ekkofood1.jpg',
    shortBio: 'Líder en coordinación de operaciones, cursos y experiencias ecológicas',
    fullBio: 'Beatriz Llerena es líder en coordinación de operaciones, cursos y experiencias ecológicas. Su experiencia internacional y visión estratégica han sido fundamentales para el crecimiento y desarrollo de Ekkofood como referente en agricultura sostenible.',
    linkedIn: 'https://www.linkedin.com/in/beatrizllerena/',
    badge: {
      text: 'Coordinadora desde 2000',
      color: '#c2b8a8'
    },
    stats: [
      {
        title: 'Gestión Integral',
        description: 'Supervisa operaciones, procesos y coordinación del equipo en Ekkofood.',
        icon: <WorkspacePremiumIcon sx={{ color: '#eff1ee' }} />
      },
      {
        title: 'Retiros y Bienestar',
        description: 'Organiza experiencias que combi',
        icon: <HealthAndSafetyIcon sx={{ color: '#eff1ee' }} />
      }
    ]
  },
  {
    id: 4,
    slug: 'jose-llerena',
    name: 'José Llerena',
    role: 'Producción y Mantenimiento',
    memberLabel: 'Producción y Mantenimiento',
    image: '/José - Ekkofood.jpg',
    shortBio: 'Responsable de producción ecológica, mantenimiento y distribución de la finca',
    fullBio: 'José Llerena es responsable de producción ecológica, mantenimiento y distribución de la finca. Su enfoque innovador y atención al detalle son fundamentales para optimizar los procesos productivos sostenibles.',
    badge: {
      text: 'Especialista en Producción',
      color: '#c2b8a8'
    },
    stats: [
      {
        title: 'Producción Ecológica',
        description: 'Supervisa cultivos sostenibles, incluyendo 500 árboles de granado ecológico.',
        icon: <AgricultureIcon sx={{ color: '#eff1ee' }} />
      },
      {
        title: 'Innovación y Detalle',
        description: 'Aplica creatividad y atención al detalle para optimizar procesos en la finca.',
        icon: <VerifiedIcon sx={{ color: '#eff1ee' }} />
      }
    ]
  }
];

const ContentSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0, 12, 0),
  background: 'linear-gradient(135deg, rgba(194, 184, 168, 0.12) 0%, rgba(194, 184, 168, 0.06) 50%, #ffffff 100%)',
  minHeight: '80vh',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(6, 0, 10, 0),
    minHeight: '70vh',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4, 0, 8, 0),
    minHeight: '60vh',
  },
  [theme.breakpoints.down('xs')]: {
    padding: theme.spacing(3, 0, 6, 0),
    minHeight: 'auto',
  },
}));

const ProfileContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  background: '#ffffff',
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(194, 184, 168, 0.08)',
  overflow: 'hidden',
  margin: theme.spacing(6, 0),
  minHeight: '500px',
  gap: theme.spacing(8),
  padding: theme.spacing(6),
  transition: 'all 1.2s cubic-bezier(0.16, 1, 0.3, 1)',
  transform: 'translateY(0)',
  opacity: 1,
  [theme.breakpoints.down('lg')]: {
    gap: theme.spacing(6),
    padding: theme.spacing(5),
  },
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    minHeight: 'auto',
    gap: theme.spacing(4),
    padding: theme.spacing(4),
    margin: theme.spacing(4, 0),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
    margin: theme.spacing(3, 0),
    gap: theme.spacing(3),
  },
  [theme.breakpoints.down('xs')]: {
    padding: theme.spacing(2),
    margin: theme.spacing(2, 0),
    gap: theme.spacing(2),
  },
  '&.slide-enter': {
    opacity: 0,
    transform: 'translateX(30px)',
  },
  '&.slide-enter-active': {
    opacity: 1,
    transform: 'translateX(0)',
    transition: 'all 1.2s cubic-bezier(0.16, 1, 0.3, 1)',
  },
  '&.slide-exit': {
    opacity: 1,
    transform: 'translateX(0)',
  },
  '&.slide-exit-active': {
    opacity: 0,
    transform: 'translateX(-30px)',
    transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
  },
}));

const ImageSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '400px',
  height: '500px',
  flexShrink: 0,
  overflow: 'hidden',
  [theme.breakpoints.down('lg')]: {
    width: '450px',
    height: '550px',
  },
  [theme.breakpoints.down('md')]: {
    width: '100%',
    maxWidth: '500px',
    height: '400px',
    alignSelf: 'center',
  },
  [theme.breakpoints.down('sm')]: {
    height: '350px',
    maxWidth: '400px',
  },
  [theme.breakpoints.down('xs')]: {
    height: '300px',
    maxWidth: '100%',
  },
}));

const ProfileImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  objectPosition: 'center',
  clipPath: 'url(#differentone23)',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
  },
}));

const BadgeOverlay = styled('img')(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(0),
  right: theme.spacing(0),
  width: '100px',
  height: '100px',
  zIndex: 2,
  borderRadius: '50%',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
  [theme.breakpoints.down('md')]: {
    width: '80px',
    height: '80px',
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  [theme.breakpoints.down('sm')]: {
    width: '60px',
    height: '60px',
    top: theme.spacing(0.5),
    right: theme.spacing(0.5),
  },
}));


const ContentSection2 = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  paddingTop: theme.spacing(2),
}));

const StatsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(4),
  marginBottom: theme.spacing(3),
  flexWrap: 'wrap',
  [theme.breakpoints.down('md')]: {
    gap: theme.spacing(3),
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(2.5),
  },
  [theme.breakpoints.down('xs')]: {
    gap: theme.spacing(2),
  },
}));

const StatItem = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  minWidth: '250px',
  [theme.breakpoints.down('sm')]: {
    minWidth: 'auto',
    gap: theme.spacing(1.5),
  },
  [theme.breakpoints.down('xs')]: {
    gap: theme.spacing(1),
  },
}));

const StatIcon = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: '50%',
  backgroundColor: `#455b40`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  [theme.breakpoints.down('sm')]: {
    width: 40,
    height: 40,
  },
  [theme.breakpoints.down('xs')]: {
    width: 36,
    height: 36,
  },
}));


const ContactButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: '#ffffff',
  padding: theme.spacing(1.5, 4),
  borderRadius: '25px',
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: 'none',
  fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1rem' },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 3),
  },
  [theme.breakpoints.down('xs')]: {
    padding: theme.spacing(0.8, 2.5),
    fontSize: '0.8rem',
  },
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    boxShadow: '0 4px 12px rgba(124, 179, 66, 0.3)',
    transform: 'translateY(-1px)',
  },
  transition: 'all 0.3s ease',
}));

const QuienesSomos: React.FC = () => {
  const navigate = useNavigate();

  // Update meta tags for Quiénes Somos page
  useMetaTags({
    title: 'Quiénes Somos - Equipo EKKOFOOD',
    description: 'Conoce al equipo que hace posible la transformación hacia una agricultura más sostenible. Expertos en agricultura ecológica, permacultura y sostenibilidad.',
    image: 'https://ekkofood.com/paisaje-ekko1.jpg',
    url: 'https://ekkofood.com/quienes-somos',
    type: 'website'
  });

  return (
    <>
      <PageHero 
        title="Quiénes Somos"
        subtitle="Ekkofood - El Equipo"
        description="Conoce al equipo que hace posible la transformación hacia una agricultura más sostenible"
        backgroundImage="/paisaje-ekko1.jpg"
        height="400px"
        showButton={false}
        contentAlign="center"
        overlay={true}
        overlayOpacity={0.4}
        showBreadcrumbs={true}
        breadcrumbs={[
          { label: "Inicio", link: "/" },
          
          { label: "Quiénes Somos", link: "/quienes-somos" }
        ]}
      />
      
      <ContentSection>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          {/* Introduction */}
          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              gutterBottom 
              sx={{ 
                color: '#455b40', 
                fontWeight: 700, 
                mb: 4,
                fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2.2rem', lg: '2.5rem' },
                textAlign: 'center'
              }}
            >
              Sembrando Sostenibilidad
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              gap: 6, 
              flexDirection: { xs: 'column', md: 'row' }
            }}>
              <Box sx={{ flex: 1 }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    lineHeight: 1.5, 
                    color: 'text.secondary',
                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                    mb: 3
                  }}
                >
                  En Ekkofood trabajamos desde 1999 para transformar la agricultura hacia un modelo más ecológico, regenerativo y accesible. Somos una iniciativa familiar que, junto a expertos nacionales e internacionales, organiza cursos, talleres y programas de formación en agricultura orgánica y regenerativa.
                </Typography>
                
                <Typography 
                  variant="body1" 
                  sx={{ 
                    lineHeight: 1.5, 
                    color: 'text.secondary',
                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                    mb: 3
                  }}
                >
                  Nuestro propósito es ayudar a agricultores, técnicos y organizaciones a optimizar recursos, reducir costes y fortalecer su independencia, fomentando sistemas productivos sostenibles que no dependan de insumos externos.
                </Typography>
                
                <Typography 
                  variant="body1" 
                  sx={{ 
                    lineHeight: 1.5, 
                    color: 'text.secondary',
                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                  }}
                >
                  Desde el año 2000 contamos con el sello oficial de Agricultura Ecológica, y en 2025 fuimos reconocidos como finca miembro de Slow Farm, parte del movimiento internacional Slow Food. Estos logros refuerzan nuestro compromiso de más de dos décadas con una agricultura que respeta la tierra, las personas y los tiempos de la naturaleza.
                </Typography>
              </Box>
              
              <Box sx={{ 
                width: { xs: '100%', md: '400px' }, 
                height: '350px',
                flexShrink: 0,
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(194, 184, 168, 0.15)'
              }}>
                <img 
                  src="/familia.jpg"
                  alt="Agricultura Ecológica Certificada"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* Team Members List */}
          <Stack spacing={6}>
            {teamMembers.map((member, index) => {
              const shouldReverse = index === 1 || index === 3; // Second and fourth members (index 1 and 3)
              return (
                <Fade in={true} timeout={800} style={{ transitionDelay: `${index * 200}ms` }} key={member.id}>
                  <Box>
                    <ProfileContainer sx={{ flexDirection: shouldReverse ? { md: 'row-reverse' } : 'row' }}>
                      <ImageSection>
                      {/* Hidden SVG with clip path definition */}
                      <svg style={{ position: 'absolute', top: '-999px', left: '-999px', width: 0, height: 0 }}>
                        <defs>
                          <clipPath id={`clipPath${member.id}`} clipPathUnits="objectBoundingBox">
                            <path
                              d="M0 0H0.479167C0.766667 0 1 0.233333 1 0.520833V1H0.520833C0.233333 1 0 0.766667 0 0.479167V0Z"
                              fill="black"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                      
                      <ProfileImage 
                        src={member.image} 
                        alt={member.name}
                        sx={{ clipPath: `url(#clipPath${member.id})` }}
                      />
                      <BadgeOverlay 
                        src={index < 2 ? "/fundador.png" : "/cofundador.png"} 
                        alt={index < 2 ? "Fundador Badge" : "Cofundador Badge"}
                      />
                    </ImageSection>
                    
                    <ContentSection2>
                      <Box>
                        <Typography 
                          variant="overline" 
                          sx={{ 
                            color: '#455b40',
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            letterSpacing: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 2
                          }}
                        >
                          <LocalFloristIcon sx={{ fontSize: '1rem' }} />
                          {member.memberLabel}
                        </Typography>
                        
                        <Typography 
                          variant="h3" 
                          sx={{ 
                            fontWeight: 700, 
                            mb: 3,
                            fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.9rem', lg: '2.2rem' },
                            lineHeight: 1.3,
                            color: 'text.primary'
                          }}
                        >
                          {member.name}
                          <br />
                          {member.role}
                        </Typography>
                        
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            lineHeight: 1.7, 
                            color: 'text.secondary',
                            fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                            mb: 4,
                            maxWidth: { xs: '100%', md: '90%' }
                          }}
                        >
                          {member.shortBio}
                        </Typography>

                        {member.stats && (
                          <StatsContainer>
                            {member.stats.map((stat, statIndex) => (
                              <StatItem key={statIndex}>
                                <StatIcon>
                                  {stat.icon}
                                </StatIcon>
                                <Box>
                                  <Typography 
                                    variant="h6" 
                                    sx={{ 
                                      fontWeight: 600, 
                                      mb: 1,
                                      fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' },
                                      color: 'text.primary'
                                    }}
                                  >
                                    {stat.title}
                                  </Typography>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      color: 'text.secondary',
                                      lineHeight: 1.6,
                                      fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' }
                                    }}
                                  >
                                    {stat.description}
                                  </Typography>
                                </Box>
                              </StatItem>
                            ))}
                          </StatsContainer>
                        )}
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mt: 4 }}>
                        <Stack direction="row" spacing={2}>
                          {member.linkedIn && (
                            <ContactButton 
                              startIcon={<LinkedInIcon />}
                              onClick={() => window.open(member.linkedIn, '_blank', 'noopener,noreferrer')}
                              size="small"
                              sx={{ 
                                backgroundColor: '#455b40',
                                '&:hover': { backgroundColor: '#657c61' }
                              }}
                            >
                              LinkedIn
                            </ContactButton>
                          )}
                          <ContactButton 
                            endIcon={<VisibilityIcon />}
                            onClick={() => navigate(`/equipo/${member.slug}`)}
                            sx={{ 
                              backgroundColor: '#455b40',
                              '&:hover': { backgroundColor: '#657c61' }
                            }}
                          >
                            Ver más
                          </ContactButton>
                        </Stack>
                      </Box>
                    </ContentSection2>
                  </ProfileContainer>
                </Box>
              </Fade>
            );
            })}
          </Stack>

        </Container>
      </ContentSection>

      <Footer />
    </>
  );
};

export default QuienesSomos;